import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { reportService } from '../api/reports';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { ArrowLeft, Save, Upload } from 'lucide-react';

export default function ReportForm() {
    const { id } = useParams();
    const isEditMode = !!id;
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        file: null
    });
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEditMode);

    useEffect(() => {
        const loadReport = async () => {
            try {
                const data = await reportService.getById(id);
                setFormData({
                    title: data.title,
                    content: data.content,
                    file: null
                });
            } catch (error) {
                console.error("Failed to load report", error);
                navigate('/');
            } finally {
                setInitialLoading(false);
            }
        };

        if (isEditMode) {
            loadReport();
        } else {
            setInitialLoading(false);
        }
    }, [id, isEditMode, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const data = new FormData();
            data.append('title', formData.title);
            data.append('content', formData.content);
            if (formData.file) {
                data.append('file', formData.file);
            }

            if (isEditMode) {
                await reportService.update(id, data);
            } else {
                await reportService.create(data);
            }
            navigate('/');
        } catch (error) {
            console.error("Failed to save report", error);
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) return <div className="p-8">Loading...</div>;

    return (
        <div className="max-w-3xl mx-auto">
            <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center">
                    <Button variant="ghost" className="mr-4" onClick={() => navigate(-1)}>
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Назад
                    </Button>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {isEditMode ? 'Редактировать отчет' : 'Новый отчет'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl p-8 space-y-6">
                <Input
                    label="Заголовок"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    placeholder="Например: Отчет за Сентябрь 2024"
                />

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Содержание
                    </label>
                    <textarea
                        rows={10}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-3 border"
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        required
                        placeholder="Опишите проделанную работу..."
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Прикрепить файл
                    </label>
                    <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                        <div className="text-center">
                            <Upload className="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
                            <div className="mt-4 flex text-sm leading-6 text-gray-600 justify-center">
                                <label
                                    htmlFor="file-upload"
                                    className="relative cursor-pointer rounded-md bg-white font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 hover:text-blue-500"
                                >
                                    <span>Загрузить файл</span>
                                    <input
                                        id="file-upload"
                                        name="file-upload"
                                        type="file"
                                        className="sr-only"
                                        onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                                    />
                                </label>
                                <p className="pl-1">или перетащите сюда</p>
                            </div>
                            <p className="text-xs leading-5 text-gray-600">PDF, DOCX, XLS до 10MB</p>
                            {formData.file && (
                                <p className="mt-2 text-sm text-green-600 font-semibold">
                                    Выбран: {formData.file.name}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-100">
                    <Button type="button" variant="secondary" className="mr-3" onClick={() => navigate('/')}>
                        Отмена
                    </Button>
                    <Button type="submit" disabled={loading}>
                        <Save className="w-4 h-4 mr-2" />
                        {loading ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                </div>
            </form>
        </div>
    );
}
